import { Box } from '@chakra-ui/react'
import type { WizardVehicleAdditionalData } from 'BrokerTsLib/src/dtos'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from '@/components/Loading'
import { LoadingFailedAlert } from '@/components/LoadingFailedAlert'
import { Note } from '@/components/Note'
import { Steps } from '@/components/Steps/Steps'
import { WizzardHeader } from '@/components/WizzardHeader/WizzardHeader'
import { Step3 } from '@/containers/steps/vehicle/extra/Step3'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { QuotesProvider } from '@/context/QuotesContext'
import { SelectedQuoteProvider } from '@/context/SelectedQuoteContext'
import { SidebarProvider } from '@/context/SidebarContext'
import { StepsProvider } from '@/context/StepsContext'
import { WizzardHeaderProvider } from '@/context/WizzardHeaderContext'
import { WithAuth } from '@/hoc/WithAuth'
import { useGetAdditionalData } from '@/hooks/useWizard'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'

const Step3Sections = {
  vozidlo: 1,
  pojistnik: 2,
  vlastnik: 3,
  drzitel: 4,
  adresa: 5,
} as const

type Step3Sections = typeof Step3Sections[keyof typeof Step3Sections]

export { Step3Sections }

const Step3PageComponent = () => {
  const navigate = useNavigate()
  const [noteText, setNoteText] = useState<string | undefined>(undefined)

  const {
    insuranceParams,
    insuranceParamsId: paramsId,
    isLoadingData,
    isFetchingData,
    isContracted,
    error
  } = useInsuranceParams()
  const {
    isLoading: isLoadingFormData,
    data: formData,
    refetch: refetchFormData,
  } = useGetAdditionalData(paramsId!, {
    enabled: false,
    cacheTime: 0,
    retry: false,
  })

  const data = formData?.data as WizardVehicleAdditionalData

  useEffect(() => {
    if (!insuranceParams) return

    if (
      !isContracted &&
      !insuranceParams.generalParams.selectedOfferId &&
      !((insuranceParams.generalParams.lastWizardStep ?? 0) >= 3) &&
      !isFetchingData
    ) {
      navigate(`/extra/steps/2/${paramsId}`)
      return
    }

    refetchFormData()
  }, [insuranceParams, isFetchingData])

  const handleCreateNote = () => {
    setNoteText('Nová poznámka')
  }

  return (
    <StepsProvider currentStepId={3}>
      <SidebarProvider>
        <WizzardHeaderProvider>
          <QuotesProvider>
            {isLoadingData && <Loading large={true} />}
            {!isLoadingData && error && <LoadingFailedAlert />}
            {insuranceParams && (
              <SelectedQuoteProvider>
                <PageWrapper
                  steps={<Steps />}
                  header={<WizzardHeader onCreateNote={handleCreateNote} />}
                  sidebar={<Sidebar />}
                  contentProps={{
                    sx: {
                      pt: { base: 0, md: 0 },
                    },
                  }}
                >
                  {noteText && noteText !== '' && (
                    <Note
                      date="11.10.2021 14:35"
                      text={noteText}
                      onChange={(text) => setNoteText(text)}
                    />
                  )}

                  <Box sx={{ pt: { base: 0, md: !noteText ? 8 : 0 } }}>
                    {isLoadingFormData && <Loading large={true} />}
                    {!isLoadingFormData && data && <Step3 data={data} />}
                  </Box>
                </PageWrapper>
              </SelectedQuoteProvider>
            )}
          </QuotesProvider>
        </WizzardHeaderProvider>
      </SidebarProvider>
    </StepsProvider>
  )
}

export const Step3Page = WithAuth(Step3PageComponent)
